import { TablesExternalAssetsDisplayGridRowData } from '@tableau/data-catalog-client-components';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { updateOrDeleteCertificationForItem } from '~src/catalog/restApi/dataQuality/setDataQualityCertificationForItem';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';

import { Messages } from '../../codegen/Localize';
import { IActionResource, IListResource } from '../../httpClient/resources/Resource';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { ILuidItemInfo } from '../../spacesPlatform/models/resourceInfoParts';
import { changeTagsOnCatalogContent } from '../CatalogTagsHelper';
import { TablesGraphQLResourceQuery } from '../fetchers/TablesGraphQLResourceQuery';
import { DeleteTablesRequest } from '../serverApi/requests/databases/DeleteTablesRequest';
import { GetTableActionsRequest } from '../serverApi/requests/databases/GetTableActionsRequest';
import { ITableCertificationParams } from '../serverApi/requests/databases/SetTableCertificationRequest';
import { ITableDescriptionParams, SetTableDescriptionRequest } from '../serverApi/requests/databases/SetTableDescriptionRequest';
import { ITableContactParams, SetTablesContactRequest } from '../serverApi/requests/databases/SetTablesContactRequest';
import { TableAllowedActions } from '../serverApi/types/TableAllowedActions';


class TablesResource implements IActionResource<TableAllowedActions>, IListResource<TablesExternalAssetsDisplayGridRowData> {

  private allQuery = new TablesGraphQLResourceQuery();

  public all(): TablesGraphQLResourceQuery {
    return this.allQuery;
  }

  public actions(id: string): Promise<TableAllowedActions> {
    if (FeatureFlags.dataCatalogPermissionEnabled) {
      return WebHttpClient.sendRequest(new GetTableActionsRequest(id));
    } else {
      return Promise.resolve({
        setDescription: false,
        setPermissions: false,
        updateRemoveDataQuality: false,
        sendEmail: false,
        share: false,
        setProject: false
      });
    }
  }

  public deleteTable = async (id: string, name: string): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new DeleteTablesRequest({ ids: [id] }))
      .then(r => this.toast(r, Messages.removeExternalContentSuccessful({ ITEM_NAME: name }), Messages.removeFailed()));
  };

  public setTableDescription = async (args: ITableDescriptionParams): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetTableDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessfulWithWait(), Messages.editDescriptionFailure()));
  };

  public setTableCertification = async (args: ITableCertificationParams): Promise<IResult> => {
    return updateOrDeleteCertificationForItem({ ...args, contentType: 'table' })
      .then(r => this.toast(r, Messages.editCertificationSuccessful(), Messages.editCertificationFailure()));
  };

  public setContact = async (args: ITableContactParams): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetTablesContactRequest(args));
  };

  public getIconForTableType(): string {
    return imgPath('table_row.svg');
  }

  public async changeTags(params: {
    tables: ReadonlyArray<ILuidItemInfo>;
    tagsToAdd: ReadonlyArray<string>;
    tagsToRemove: ReadonlyArray<string>;
  }): Promise<void> {
    const { tagsToAdd, tagsToRemove, tables } = params;
    const luids = tables.map(t => t.luid);
    return changeTagsOnCatalogContent({ luids, contentType: 'table', tagsToAdd, tagsToRemove });
  }

  public async changeTagsOnColumns(params: {
    columnLuids: ReadonlyArray<string>;
    tagsToAdd: ReadonlyArray<string>;
    tagsToRemove: ReadonlyArray<string>;
  }): Promise<void> {
    const { tagsToAdd, tagsToRemove, columnLuids } = params;
    return changeTagsOnCatalogContent({ luids: columnLuids, contentType: 'column', tagsToAdd, tagsToRemove });
  }

  private toast(result: IResult, successMessage: string, failureMessage: string): any {
    if (result.errors && result.errors.length > 0) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: failureMessage
      });
    } else {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: successMessage
      });
    }
  }
}

export const Tables = new TablesResource();
export type Tables = typeof Tables;
